/**
 * AssessmentCard — status of the work-style assessment invite for roles that
 * take it (service advisor, general manager): chip, timestamps, Send/Resend.
 * Rendered only when `roleTakesAssessment(lead.role)`.
 */
import { useId } from 'react';
import { ClipboardCheck, Loader2, Send } from 'lucide-react';
import { errorMessage, useToast } from '@/components/Toast';
import { fullDateTime, relativeTime } from '@/lib/time';
import type { AssessmentSummary } from '@/features/pipeline/types';
import { assessmentStatusMeta, assessmentStatusOf } from './assessment-format';
import { useSendAssessment } from './messages-api';

function Stamp({ label, at }: { label: string; at: string | null }) {
  if (!at) return null;
  return (
    <div className="flex gap-3 py-1 text-[13px]">
      <dt className="w-24 shrink-0 text-muted">{label}</dt>
      <dd className="text-gray-900">
        <time dateTime={at} title={fullDateTime(at)}>
          {relativeTime(at)}
        </time>
      </dd>
    </div>
  );
}

export function AssessmentCard({ leadId, assessment }: { leadId: string; assessment: AssessmentSummary | null }) {
  const id = useId();
  const toast = useToast();
  const send = useSendAssessment(leadId);
  const status = assessmentStatusOf(assessment);
  const meta = assessmentStatusMeta(status);
  const resend = status !== 'not_sent';

  const onSend = () =>
    send.mutate(undefined, {
      onSuccess: () => toast.push({ kind: 'success', title: resend ? 'Assessment link sent again' : 'Assessment link sent' }),
      onError: (err) => toast.push({ kind: 'error', title: 'Could not send the assessment', message: errorMessage(err) }),
    });

  return (
    <section aria-labelledby={`${id}-h`} className="rounded-lg border border-gray-200 bg-white" data-testid="assessment-card">
      <div className="flex items-center justify-between gap-2 px-3 pt-2.5">
        <h3 id={`${id}-h`} className="text-[13px] font-semibold text-gray-900">
          Assessment
        </h3>
        <span className={`rounded-full px-2 py-0.5 text-[12px] font-medium ${meta.chip}`} data-testid="assessment-status">
          {meta.label}
        </span>
      </div>
      <div className="px-3 pb-3 pt-2">
        <div className="flex items-start gap-2.5">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-navy/10 text-navy">
            <ClipboardCheck size={18} strokeWidth={2} aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] text-gray-900">Work-style assessment</p>
            <p className="text-[12px] text-muted">{meta.hint}</p>
          </div>
        </div>
        {assessment && (
          <dl className="mt-2 divide-y divide-gray-100 border-t border-gray-100">
            <Stamp label="Sent" at={assessment.sent_at} />
            <Stamp label="Opened" at={assessment.opened_at} />
            <Stamp label="Completed" at={assessment.completed_at} />
          </dl>
        )}
        <div className="-mx-1 mt-2">
          <button
            type="button"
            onClick={onSend}
            disabled={send.isPending}
            className="inline-flex min-h-10 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium text-navy hover:bg-page disabled:opacity-50"
          >
            {send.isPending ? (
              <Loader2 size={14} strokeWidth={2} className="animate-spin" aria-hidden />
            ) : (
              <Send size={14} strokeWidth={2} aria-hidden />
            )}
            {send.isPending ? 'Sending…' : resend ? 'Resend link' : 'Send assessment'}
          </button>
        </div>
      </div>
    </section>
  );
}
